<template>
  <div class="portal-page terms-page">
    <div class="portal-page__inner terms-page__shell">
      <header class="terms-page__head">
        <p class="terms-page__meta">{{ COMPANY.serviceName }}</p>
        <h1 class="terms-page__title">{{ doc.title }}</h1>
        <p v-if="doc.summary" class="terms-page__desc">{{ doc.summary }}</p>
        <p class="terms-page__date">시행일 {{ doc.updatedAt }}</p>
        <nav class="terms-page__docs" aria-label="다른 약관">
          <router-link
            v-for="item in PORTAL_LEGAL_LINKS"
            :key="item.to"
            :to="item.to"
            class="terms-page__doc"
            :class="{ 'is-on': item.type === docType }"
          >
            {{ item.label }}
          </router-link>
        </nav>
      </header>

      <article class="terms-page__panel">
        <div v-if="cmsBody" class="terms-page__cms">
          <pre class="terms-page__cms-body">{{ cmsBody }}</pre>
        </div>
        <template v-else>
          <div v-if="privacyRows" class="terms-page__table-wrap">
            <table class="terms-page__table">
              <caption class="terms-page__table-caption">개인정보 수집 항목</caption>
              <thead>
                <tr>
                  <th scope="col">구분</th>
                  <th scope="col">수집 항목</th>
                  <th scope="col">수집 목적</th>
                  <th scope="col">보유 기간</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in privacyRows" :key="row.category">
                  <td>{{ row.category }}</td>
                  <td>{{ row.items }}</td>
                  <td>{{ row.purpose }}</td>
                  <td>{{ row.retention }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="terms-page__sections">
            <section v-for="section in sections" :key="section.title" class="terms-page__section">
              <h2 class="terms-page__section-title">{{ section.title }}</h2>
              <p class="terms-page__section-body">{{ section.body }}</p>
            </section>
          </div>
        </template>
      </article>

      <div class="terms-page__actions">
        <router-link to="/signup" class="btn-secondary">회원가입</router-link>
        <router-link to="/" class="btn-primary">홈으로</router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { fetchLegalDocs } from '@/api/content'
import {
  COMPANY,
  EMAIL_REJECTION_SECTIONS,
  LEGAL_DOCS,
  MARKETING_CONSENT_SECTIONS,
  PORTAL_LEGAL_LINKS,
  PRIVACY_COLLECTION_ROWS,
  REFUND_POLICY_SECTIONS,
  PRIVACY_CONSENT_SECTIONS,
  PRIVACY_POLICY_SECTIONS,
  TERMS_OF_SERVICE_SECTIONS,
  type LegalDocType,
} from '@/constants/legalContent'

const TYPE_TO_CODE: Partial<Record<LegalDocType, string>> = {
  service: 'member_service',
  privacy: 'member_privacy',
  'privacy-consent': 'member_privacy_consent',
  marketing: 'member_marketing',
}

const route = useRoute()
const cmsBody = ref('')
const cmsEffective = ref('')

const docType = computed(() => route.params.type as LegalDocType)

const doc = computed(() => {
  const base = LEGAL_DOCS[docType.value] ?? LEGAL_DOCS.service
  if (cmsEffective.value) {
    return { ...base, updatedAt: cmsEffective.value }
  }
  return base
})

const sections = computed(() => {
  switch (docType.value) {
    case 'privacy':
      return PRIVACY_POLICY_SECTIONS
    case 'privacy-consent':
      return PRIVACY_CONSENT_SECTIONS
    case 'marketing':
      return MARKETING_CONSENT_SECTIONS
    case 'refund':
      return REFUND_POLICY_SECTIONS
    case 'email':
      return EMAIL_REJECTION_SECTIONS
    default:
      return TERMS_OF_SERVICE_SECTIONS
  }
})

const privacyRows = computed(() =>
  docType.value === 'privacy-consent' ? PRIVACY_COLLECTION_ROWS : undefined,
)

async function loadCms() {
  cmsBody.value = ''
  cmsEffective.value = ''
  const code = TYPE_TO_CODE[docType.value]
  if (!code) return
  try {
    const docs = await fetchLegalDocs('MEMBER')
    const hit = docs.find((row) => row.code === code && row.active)
    if (hit?.body) {
      cmsBody.value = hit.body
      cmsEffective.value = hit.effective
    }
  } catch {
    /* 로컬 폴백 */
  }
}

watch(docType, loadCms, { immediate: true })
</script>

<style lang="scss" src="./TermsPage.scss"></style>
